// On boot, merge the offline entity cache into Redux so lists render before network.
import _ from "lodash";
import {
  cacheGetCurrentUser,
  cacheGetSlice,
  cacheGetUrl,
  guessSliceFromUrl,
} from "./cache";
import { GET_CURRENT_USER } from "modules/current_user/actions";

// Slices used heavily on mobile — hydrate into Redux on boot
const MOBILE_SLICES = [
  "custom_form_records",
  "custom_forms",
  "custom_forms_menus",
  "project_qa_action_items",
  "project_qa_paint_inspections",
  "project_qa_welding_inspections",
  "project_qa_site_site_installations",
  "project_qa_site_surveillances",
  "project_drawings",
  "project_areas",
  "projects",
  "employees",
  "users",
  "store_bookout_items",
  "store_overalls",
  "store_requests",
  "store_overalls_statuses",
  "store_overalls_garments",
  "project_qa_action_items_statuses",
  "project_qa_welding_inspections_welding_types",
  "project_qa_welding_inspections_welding_processes",
  "project_qa_welding_inspections_welding_stages",
  "project_qa_welding_inspections_welding_standards",
  "project_qa_welding_inspections_welding_procedures",
  "project_qa_welding_inspections_access_restrictions",
  "project_qa_welding_inspections_viewing_conditions",
  "project_qa_welding_inspections_surface_conditions",
  "project_qa_welding_inspections_fillet_sizes",
  "project_qa_welding_inspections_inspection_specs",
  "project_qa_welding_inspections_inspection_criterias",
  "project_qa_welding_inspections_failure_reasons",
  "workshops",
  "paint_locations",
  "project_technical_details_paint_systems",
  "address_book_companies",
  "approvals",
  "approval_steps",
  "mw_jobs",
  "mw_products",
];

const sliceToListAction = {
  custom_form_records: "get_custom_form_records",
  custom_forms: "get_custom_forms",
  custom_forms_menus: "get_custom_forms_menus",
  project_qa_action_items: "get_project_qa_action_items",
  project_qa_paint_inspections: "get_project_qa_paint_inspections",
  project_qa_welding_inspections: "get_project_qa_welding_inspections",
  project_qa_site_site_installations: "get_project_qa_site_site_installations",
  project_qa_site_surveillances: "get_project_qa_site_surveillances",
  project_drawings: "get_project_drawings",
  project_areas: "get_project_areas",
  projects: "get_projects",
  employees: "get_employees",
  users: "get_user_managements",
  store_bookout_items: "get_store_bookout_items",
  store_overalls: "get_store_overalls",
  store_requests: "get_store_requests",
  store_overalls_statuses: "get_store_overalls_statuses",
  store_overalls_garments: "get_store_overalls_garments",
  project_qa_action_items_statuses: "get_project_qa_action_items_statuses",
  project_qa_welding_inspections_welding_types: "get_project_qa_welding_inspections_welding_types",
  project_qa_welding_inspections_welding_processes: "get_project_qa_welding_inspections_welding_processes",
  project_qa_welding_inspections_welding_stages: "get_project_qa_welding_inspections_welding_stages",
  project_qa_welding_inspections_welding_standards: "get_project_qa_welding_inspections_welding_standards",
  project_qa_welding_inspections_welding_procedures: "get_project_qa_welding_inspections_welding_procedures",
  project_qa_welding_inspections_access_restrictions: "get_project_qa_welding_inspections_access_restrictions",
  project_qa_welding_inspections_viewing_conditions: "get_project_qa_welding_inspections_viewing_conditions",
  project_qa_welding_inspections_surface_conditions: "get_project_qa_welding_inspections_surface_conditions",
  project_qa_welding_inspections_fillet_sizes: "get_project_qa_welding_inspections_fillet_sizes",
  project_qa_welding_inspections_inspection_specs: "get_project_qa_welding_inspections_inspection_specs",
  project_qa_welding_inspections_inspection_criterias: "get_project_qa_welding_inspections_inspection_criterias",
  project_qa_welding_inspections_failure_reasons: "get_project_qa_welding_inspections_failure_reasons",
  workshops: "get_workshops",
  paint_locations: "get_paint_locations",
  project_technical_details_paint_systems: "get_project_technical_details_paint_systems",
  address_book_companies: "get_address_book_companies",
  approvals: "get_approvals",
  approval_steps: "get_approval_steps",
  mw_jobs: "GET_MW_JOBS",
  mw_products: "GET_MW_PRODUCTS",
};

export const applyFetchedDataToStore = (store, url, data) => {
  if (!store || data == null) return;
  const slice = guessSliceFromUrl(url);
  if (!slice) return;

  if (slice === "current_user") {
    store.dispatch({ type: `${GET_CURRENT_USER}_FULFILLED`, payload: { data } });
    return;
  }

  const typeBase = sliceToListAction[slice];
  if (_.isArray(data) && typeBase) {
    store.dispatch({ type: `${typeBase}_FULFILLED`, payload: { data } });
    return;
  }

  if (_.isObject(data) && data.id != null) {
    store.dispatch({ type: "offline_entity_replaced", payload: { slice, data } });
  }
};

export const hydrateStoreFromCache = async (store) => {
  if (!store) return;

  const user = await cacheGetCurrentUser();
  if (!_.isEmpty(user)) {
    store.dispatch({
      type: `${GET_CURRENT_USER}_FULFILLED`,
      payload: { data: user },
    });
  }

  for (const slice of MOBILE_SLICES) {
    const entities = await cacheGetSlice(slice);
    if (_.isEmpty(entities)) continue;
    const typeBase = sliceToListAction[slice];
    if (!typeBase) continue;
    store.dispatch({
      type: `${typeBase}_FULFILLED`,
      payload: { data: entities },
    });
  }
};

export const hydrateCurrentUserOnly = async () => {
  return cacheGetCurrentUser();
};

export const getCachedUrlData = async (url) => {
  const hit = await cacheGetUrl("GET", url);
  return hit?.data;
};
